/**
 * Hook to determine link flow configuration based on feature flags.
 * Centralizes the logic for when to show organization selection.
 */

import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import { PostHogFeatureFlag } from '#~/posthog/constants.js';
import { useMemo } from 'react';

export interface LinkFlowConfig {
  /**
   * Whether organization selection should be enabled in the link flow.
   */
  enableOrganizationSelection: boolean | undefined;
}

/**
 * Hook to get link flow configuration based on feature flags.
 *
 * @returns Configuration object for the link flow
 *
 * @example
 * ```tsx
 * const config = useLinkFlowConfig();
 * if (config.enableOrganizationSelection) {
 *   // Show organization selector
 * }
 * ```
 */
export function useLinkFlowConfig(): LinkFlowConfig {
  const { enabled: organizationsFeatureEnabled } = useFeatureFlag(
    PostHogFeatureFlag.ORGANIZATIONS_FEATURE,
    { defaultValue: false }
  );

  return useMemo(
    () => ({
      enableOrganizationSelection: organizationsFeatureEnabled
    }),
    [organizationsFeatureEnabled]
  );
}
